const _excluded = ["ref"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Button, Callout, CustomModal, TableList, TextField, Typography, useGlobalContext } from 'seagull';
import { getRequiredValidator, richTextValidation, titleValidation } from 'validators';
import { useOnMount } from "../../hooks/useOnMount";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { getIntercomHelpArticle, handlePromise, sendErrReport } from "../../infra/utils";
import { useStoreActions } from "../../store/store-context";
import styles from "./AddCreditModal.module.scss";
import Decimal from 'decimal.js';
import { timeModel, MoneyModel } from "../../models";
import { utils } from "../../utils";
import ExternalLinkButton from "../ExternalLinkButton";
const testPrefix = 'add-credit-modal';
const headerItems = [{
  value: 'Service name'
}, {
  value: 'Total'
}];
const columnDefs = [{
  width: 450
}, {
  width: 125
}];
const getShouldShowFieldError = (fieldState, formState) => fieldState.error && (fieldState.isTouched || formState.isSubmitted);
export default function AddCreditModal(_ref) {
  let {
    dismissModal,
    relationship,
    nextBillingDate,
    onRefresh
  } = _ref;
  const {
    control,
    formState,
    handleSubmit,
    setValue
  } = useForm({
    mode: 'all',
    defaultValues: {
      note: '',
      price: null
    }
  });
  const globalContext = useGlobalContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const {
    logBI,
    api
  } = useStoreActions();
  const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  useOnMount(() => {
    serviceTemplateApi.get({
      includeAdhocCharges: true,
      includeDeleted: false
    }).then(services => {
      setServiceTemplates(services.raw);
      setLoadingServiceTemplates(false);
    });
  });

  // service templates
  const serviceTemplateOptions = useMemo(() => {
    if (loadingServiceTemplates) return [];
    const options = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.map(st => ({
      value: st,
      label: st.name
    }));
    return sortBy(options, o => o.label);
  }, [serviceTemplates, loadingServiceTemplates]);
  const getServiceOptionLabel = option => {
    if (!option) return '';
    if (typeof option == 'string') {
      return option;
    } else {
      return option.label;
    }
  };
  const applyCredit = async _ref2 => {
    var _price$amount;
    let {
      name,
      note,
      price,
      serviceTemplateId
    } = _ref2;
    if (!nextBillingDate) return;
    logBI(18001, {
      title: name,
      note,
      price: price === null || price === void 0 ? void 0 : price.toNumber()
    });
    const loaderName = 'addCreditLoader';
    globalContext.startLoader(loaderName);
    const rate = ((_price$amount = price === null || price === void 0 ? void 0 : price.amount()) !== null && _price$amount !== void 0 ? _price$amount : new Decimal(0)).negated();
    const result = await handlePromise(api.charge.create(relationship.id, {
      temp_price: new MoneyModel(rate).toDTO(),
      quantity: 100,
      rate: utils.dollarsToCents(rate),
      serviceTemplateId,
      name,
      note,
      chargeOn: timeModel.toDatestampArray(nextBillingDate)
    }));
    if (!result.success) {
      globalContext.showErrorToast('Failed to add credit due to an unexpected error', "".concat(testPrefix, "-error-toast"));
      sendErrReport({
        type: 'chargeError',
        error: result.error
      });
    } else {
      globalContext.showSuccessToast("".concat(price ? price.abs().display() : '', " credit was added to ").concat(relationship.customer.name), "".concat(testPrefix, "-success-toast"));
      await onRefresh();
      dismissModal({
        status: 'success'
      });
    }
    globalContext.stopLoader(loaderName);
  };
  return /*#__PURE__*/React.createElement(CustomModal, {
    className: styles.modal,
    header: /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testPrefix, "-header"),
      className: styles.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h6",
      weight: "bold"
    }, "Add Credit")),
    footer: /*#__PURE__*/React.createElement("div", {
      className: styles.actions
    }, /*#__PURE__*/React.createElement(Button, {
      type: "submit",
      "data-testid": "".concat(testPrefix, "-add-credit-button"),
      loading: formState.isSubmitting,
      form: testPrefix
    }, "Add credit")),
    width: "784px",
    height: "524px",
    "data-testid": testPrefix,
    onClose: () => dismissModal({
      status: 'canceled'
    }),
    headerClassName: styles.blueHeader
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit(applyCredit),
    id: testPrefix,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Callout, null, "Credit added goes towards the associated agreement and gets applied automatically to future invoices.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
    openInNewTab: true,
    underline: "none",
    href: getIntercomHelpArticle('6118071-how-to-apply-a-credit-for-your-client')
  }, "Learn more about how credits work")), /*#__PURE__*/React.createElement("div", {
    className: styles.formFields
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  }), /*#__PURE__*/React.createElement(TableList.Row, {
    items: [{
      value: /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "name",
        rules: titleValidation('', {
          required: true
        }),
        render: _ref3 => {
          let {
            field,
            fieldState,
            formState
          } = _ref3;
          return /*#__PURE__*/React.createElement(Autocomplete, {
            "data-testid": "".concat(testPrefix, "-dropdown-service-selector"),
            textFieldProps: {
              inputRef: field.ref,
              error: getShouldShowFieldError(fieldState, formState)
            },
            getOptionSelected: (option, value) => {
              return option == value;
            },
            onInputChange: (_, newOption) => {
              setValue('serviceTemplateId', null);
              field.onChange(newOption);
              field.onBlur();
            },
            onChange: (_, newOption) => {
              if (!newOption || typeof newOption == 'string') return;
              const {
                id,
                name
              } = newOption.value;
              setValue('serviceTemplateId', id);
              field.onChange(name);
              field.onBlur();
            },
            options: serviceTemplateOptions,
            renderOption: getServiceOptionLabel,
            getOptionLabel: getServiceOptionLabel,
            loading: loadingServiceTemplates,
            selectOnFocus: true,
            disableClearable: false,
            multiple: false,
            freeSolo: true,
            size: "small"
          });
        }
      })
    }, {
      value: /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "price",
        rules: {
          validate: {
            required: getRequiredValidator(),
            positiveAmount: value => !!value && value.amount().gt(0)
          }
        },
        render: _ref4 => {
          let {
            field: {
              value,
              ref,
              onBlur,
              onChange: _onChange
            },
            fieldState,
            formState
          } = _ref4;
          return /*#__PURE__*/React.createElement(TextField, {
            isMoney: true,
            size: "small",
            prefix: "$",
            placeholder: "0.00",
            value: value != null ? value.toFixed() : '',
            inputRef: ref,
            onBlur: onBlur,
            testid: "".concat(testPrefix, "-cost-input"),
            error: getShouldShowFieldError(fieldState, formState),
            onChange: e => {
              const dollarsAsString = e.currentTarget.value;
              const newValue = dollarsAsString ? new MoneyModel(new Decimal(dollarsAsString)) : null;
              _onChange(newValue);
            }
          });
        }
      })
    }]
  }))), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "note",
    rules: richTextValidation('Note'),
    render: _ref5 => {
      let {
          field: {
            ref
          },
          fieldState
        } = _ref5,
        restField = _objectWithoutProperties(_ref5.field, _excluded);
      return /*#__PURE__*/React.createElement(TextField, _extends({}, restField, {
        "data-testid": "".concat(testPrefix, "-note-input"),
        label: "Note (optional)",
        multiline: true,
        minRows: 3,
        maxRows: 3,
        inputRef: ref,
        error: getShouldShowFieldError(fieldState, formState),
        helperText: getShouldShowFieldError(fieldState, formState) && fieldState.error.message
      }));
    }
  }))));
}